import type { QueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { TabSettlementResult } from '@/types'
import {
  dispatchCustomerReceiptPrint,
  getCashierNameFromStorage,
  parseReceiptSettings,
  type CustomerReceiptSettings,
} from '@/lib/printCustomerReceipt'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { escapeHtml } from '@/lib/printReport'
import { formatDateTimeHuman } from '@/lib/utils'

/**
 * F-MM-N30 — the slip a guest takes away after paying down their tab. Not a
 * tax invoice: each bill was invoiced on the day it was eaten. Prints through
 * the same transport as the customer receipt.
 */
export interface TabPaymentSlipOptions {
  /** Online provider label (Easypaisa / JazzCash …) when one was picked. */
  subMethodLabel?: string | null
  referenceNumber?: string | null
  cashierName?: string
  paidAt?: Date
  formatAmount: (n: number) => string
}

const METHOD_LABEL: Record<TabSettlementResult['method'], string> = {
  cash: 'Cash',
  card: 'Card',
  online: 'Online',
}

export function tabPaymentMethodLabel(method: TabSettlementResult['method'], subMethodLabel?: string | null): string {
  const base = METHOD_LABEL[method] ?? method
  return subMethodLabel ? `${base} · ${subMethodLabel}` : base
}

export function buildTabPaymentSlipHtml(
  result: TabSettlementResult,
  cfg: CustomerReceiptSettings,
  opts: TabPaymentSlipOptions,
): string {
  const fc = opts.formatAmount
  const paidAt = opts.paidAt ?? new Date()
  const bills = (result.allocations ?? [])
    .map(
      (a) =>
        `<tr><td>#${escapeHtml(a.order_number || a.order_id.slice(0, 8))}</td><td class="num">${escapeHtml(fc(a.amount))}</td></tr>`,
    )
    .join('')
  const ref = (opts.referenceNumber ?? '').trim()

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Tab payment</title>
<style>${stylesheet(cfg)}</style>
</head><body>
<div class="paper">
  <div class="hdr">
    ${cfg.logoUrl ? `<img class="logo" src="${escapeHtml(cfg.logoUrl)}" alt="" />` : ''}
    <div class="biz">${escapeHtml(cfg.businessName || 'Restaurant')}</div>
    ${cfg.address ? `<div class="meta">${escapeHtml(cfg.address)}</div>` : ''}
    ${cfg.phone ? `<div class="meta">${escapeHtml(cfg.phone)}</div>` : ''}
  </div>
  <div class="title">TAB PAYMENT</div>
  <table class="kv">
    ${kvRow('Guest', escapeHtml(result.customer_name))}
    ${kvRow('Date', escapeHtml(formatDateTimeHuman(paidAt)))}
    ${kvRow('Method', escapeHtml(tabPaymentMethodLabel(result.method, opts.subMethodLabel)))}
    ${ref ? kvRow('Reference', escapeHtml(ref)) : ''}
    ${kvRow('Amount paid', escapeHtml(fc(result.amount)), 'sum')}
  </table>
  ${
    bills
      ? `<div class="section">
    <div class="section-title">Bills cleared</div>
    <table class="kv">${bills}</table>
  </div>`
      : ''
  }
  <table class="kv">
    ${kvRow('Balance still owed', escapeHtml(fc(Math.max(0, result.balance_after))), 'sum')}
  </table>
  <div class="note">Payment against your tab. Each bill was invoiced on the day it was served.</div>
  <div class="footer">
    ${opts.cashierName ? `<div>Cashier: ${escapeHtml(opts.cashierName)}</div>` : ''}
    <div class="attr">software powered by bhookly.com</div>
  </div>
</div>
</body></html>`
}

export function printTabPaymentSlip(
  result: TabSettlementResult,
  cfg: CustomerReceiptSettings,
  opts: TabPaymentSlipOptions,
): { printed: boolean } {
  return { printed: dispatchCustomerReceiptPrint(buildTabPaymentSlipHtml(result, cfg, opts), cfg.paperWidthMm) }
}

/** Loads receipt branding from the shared settings cache, then prints. */
export async function printTabPaymentSlipForResult(
  queryClient: QueryClient,
  result: TabSettlementResult,
  extras: { subMethodLabel: string | null; referenceNumber: string | null },
  formatAmount: (n: number) => string,
): Promise<boolean> {
  const settingsRes = await queryClient.fetchQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })
  if (!settingsRes.success || !settingsRes.data) return false
  const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
  return printTabPaymentSlip(result, cfg, {
    subMethodLabel: extras.subMethodLabel,
    referenceNumber: extras.referenceNumber,
    cashierName: getCashierNameFromStorage(),
    formatAmount,
  }).printed
}

function kvRow(label: string, value: string, variant: 'plain' | 'sum' = 'plain'): string {
  return `<tr class="${variant === 'sum' ? 'row sum' : 'row'}"><td>${escapeHtml(label)}</td><td class="num">${value}</td></tr>`
}

function stylesheet(cfg: CustomerReceiptSettings): string {
  const baseCss = thermalPrintBaseCss({
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.fontScale,
  })
  const w = cfg.printableAreaMm
  const scale = cfg.fontScale
  return `
    ${baseCss}
    body {
      font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
      font-size: calc(11px * ${scale});
      font-weight: 700;
      line-height: 1.35;
    }
    .paper { width: ${w}mm; max-width: ${w}mm; margin: 0 auto; padding: 2mm 2mm 5mm; box-sizing: border-box; }
    .hdr { text-align: center; }
    .logo { max-width: 70%; max-height: 28mm; object-fit: contain; margin: 0 auto 2mm; display: block; }
    .biz { font-size: calc(16px * ${scale}); font-weight: 900; letter-spacing: 0.02em; margin-bottom: 1mm; }
    .meta { font-size: calc(10px * ${scale}); color: #000; }
    .title { text-align: center; font-weight: 900; font-size: calc(13px * ${scale}); margin: 3mm 0 1mm; letter-spacing: 0.05em; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 1mm 0; }
    .section { margin-top: 3mm; }
    .section-title { font-weight: 900; font-size: calc(11px * ${scale}); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #000; padding-bottom: 0.5mm; margin-bottom: 1mm; }
    table { width: 100%; border-collapse: collapse; }
    table.kv td { padding: 0.4mm 0; vertical-align: top; font-size: calc(11px * ${scale}); }
    table.kv td:last-child { text-align: right; font-variant-numeric: tabular-nums; padding-right: 1mm; }
    .row.sum td { border-top: 1px dashed #000; padding-top: 0.8mm; font-weight: 800; }
    .note { margin-top: 3mm; font-size: calc(9.5px * ${scale}); text-align: center; }
    .footer { margin-top: 4mm; padding-top: 1mm; border-top: 1px dashed #000; text-align: center; font-size: calc(9.5px * ${scale}); color: #333; }
    .footer .attr { margin-top: 1mm; font-style: italic; }
  `
}
